import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_LAYOUT, type WidgetSize } from "@/lib/dashboard-widgets";
import { onActiveUserChanged, readActiveUserID, scopedStorageKey } from "@/lib/user-scope";

export type LayoutItem = { id: string; size: WidgetSize };

const STORAGE_KEY = "nebula.dashboard.layout.v1";

function readStored(userID: string): LayoutItem[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(scopedStorageKey(STORAGE_KEY, userID));
    return raw ? (JSON.parse(raw) as LayoutItem[]) : null;
  } catch {
    return null;
  }
}
function writeStored(userID: string, items: LayoutItem[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(scopedStorageKey(STORAGE_KEY, userID), JSON.stringify(items));
}

type Ctx = {
  layout: LayoutItem[];
  editing: boolean;
  setEditing: (v: boolean) => void;
  removeAt: (idx: number) => void;
  replaceAt: (idx: number, id: string, size?: WidgetSize) => void;
  addWidget: (id: string, size: WidgetSize) => void;
  setSizeAt: (idx: number, size: WidgetSize) => void;
  reorder: (from: number, to: number) => void;
  reset: () => void;
};

const DashboardCtx = createContext<Ctx | null>(null);

export function DashboardLayoutProvider({ children }: { children: ReactNode }) {
  const [layout, setLayout] = useState<LayoutItem[]>(DEFAULT_LAYOUT);
  const [editing, setEditing] = useState(false);
  const [activeUserID, setActiveUserID] = useState<string>(() => readActiveUserID());

  useEffect(() => {
    return onActiveUserChanged((nextUserID) => {
      setActiveUserID(nextUserID);
    });
  }, []);

  useEffect(() => {
    const stored = readStored(activeUserID);
    setLayout(stored && stored.length ? stored : DEFAULT_LAYOUT);
    setEditing(false);
  }, [activeUserID]);

  const persist = useCallback((next: LayoutItem[]) => {
    setLayout(next);
    writeStored(activeUserID, next);
  }, [activeUserID]);

  const value = useMemo<Ctx>(() => ({
    layout,
    editing,
    setEditing,
    removeAt: (idx) => persist(layout.filter((_, i) => i !== idx)),
    replaceAt: (idx, id, size) =>
      persist(layout.map((it, i) => (i === idx ? { id, size: size ?? it.size } : it))),
    addWidget: (id, size) => persist([...layout, { id, size }]),
    setSizeAt: (idx, size) =>
      persist(layout.map((it, i) => (i === idx ? { ...it, size } : it))),
    reorder: (from, to) => {
      if (from === to || from < 0 || to < 0 || from >= layout.length || to >= layout.length) return;
      const next = layout.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      persist(next);
    },
    reset: () => persist(DEFAULT_LAYOUT),
  }), [layout, editing, persist]);

  return <DashboardCtx.Provider value={value}>{children}</DashboardCtx.Provider>;
}

export function useDashboardLayout() {
  const ctx = useContext(DashboardCtx);
  if (!ctx) throw new Error("useDashboardLayout must be used inside <DashboardLayoutProvider>");
  return ctx;
}
